import React from 'react';

export const DEFAULT_BRAND_APP_NAME = 'PnP FX';
export const DEFAULT_BRAND_PRIMARY_COLOR = '#0d6efd';
export const DEFAULT_BRAND_ACCENT_COLOR = '#0dcaf0';

const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeHexColor(value, fallback) {
  const color = normalizeString(value);
  return HEX_COLOR_REGEX.test(color) ? color : fallback;
}

function normalizeLogoUrl(value) {
  const logoUrl = normalizeString(value);

  if (!logoUrl || logoUrl.includes('\\') || logoUrl.startsWith('//')) {
    return '';
  }

  if (logoUrl.startsWith('/')) {
    return logoUrl;
  }

  try {
    const parsedUrl = new URL(logoUrl);
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:' ? logoUrl : '';
  } catch {
    return '';
  }
}

export function normalizeBranding(branding = {}) {
  const source = branding && typeof branding === 'object' ? branding : {};

  return {
    appName: normalizeString(source.appName) || DEFAULT_BRAND_APP_NAME,
    logoUrl: normalizeLogoUrl(source.logoUrl),
    primaryColor: normalizeHexColor(source.primaryColor, DEFAULT_BRAND_PRIMARY_COLOR),
    accentColor: normalizeHexColor(source.accentColor, DEFAULT_BRAND_ACCENT_COLOR),
  };
}

export function resolveBrandDisplayName(branding) {
  return normalizeBranding(branding).appName || DEFAULT_BRAND_APP_NAME;
}

export function resolveAdminDocumentTitle(branding) {
  return `${resolveBrandDisplayName(branding)} | Admin`;
}

export function hexToRgbString(hexColor) {
  const color = normalizeHexColor(hexColor, DEFAULT_BRAND_PRIMARY_COLOR);
  const red = Number.parseInt(color.slice(1, 3), 16);
  const green = Number.parseInt(color.slice(3, 5), 16);
  const blue = Number.parseInt(color.slice(5, 7), 16);

  return `${red}, ${green}, ${blue}`;
}

export function applyBrandingCssVariables(branding, documentElement) {
  if (!documentElement?.style?.setProperty) {
    return;
  }

  const normalized = normalizeBranding(branding);
  documentElement.style.setProperty('--bs-primary', normalized.primaryColor);
  documentElement.style.setProperty('--bs-primary-rgb', hexToRgbString(normalized.primaryColor));
  documentElement.style.setProperty('--crm-brand-primary', normalized.primaryColor);
  documentElement.style.setProperty('--crm-brand-accent', normalized.accentColor);
}

function getWordmarkParts(appName) {
  const words = appName.split(/\s+/).filter(Boolean);

  if (words.length === 2 && words.every((word) => word.length <= 4)) {
    return {
      main: words[0],
      accent: words[1],
    };
  }

  return {
    main: appName,
    accent: '',
  };
}

export function BrandedMark({
  branding,
  className = '',
  imageClassName = '',
  imageHeight = 32,
}) {
  const normalized = normalizeBranding(branding);
  const displayName = resolveBrandDisplayName(normalized);

  if (normalized.logoUrl) {
    return (
      <img
        src={normalized.logoUrl}
        alt={displayName}
        className={['crm-brand-logo', imageClassName].filter(Boolean).join(' ')}
        height={imageHeight}
      />
    );
  }

  const parts = getWordmarkParts(displayName);
  return (
    <span className={['crm-wordmark', className].filter(Boolean).join(' ')} aria-label={displayName}>
      <span className="crm-wordmark-main">{parts.main}</span>
      {parts.accent ? <span className="crm-wordmark-accent">{parts.accent}</span> : null}
    </span>
  );
}
